<template>
  <TModalCritical
    v-model:visible="visible"
    :title="t(collection ? 'edit.title' : 'new.title')"
    :is-loading="isLoading || formIsLoading"
    @close="emit('close')"
    @visible:content="handleContentVisible"
  >
    <template #default="{ dismiss }">
      <TForm @submit.prevent="handleSubmit(dismiss)">
        <TFormGroup
          :label="collection ? t('edit.label') : t('new.label')"
          label-for="favourites-newCollectionName"
          :field="r$"
        >
          <TInput
            id="favourites-newCollectionName"
            ref="collectionNameEl"
            v-model="name"
            autocomplete="off"
          />
        </TFormGroup>

        <div class="flex justify-between mt-6">
          <TButton
            v-if="collection"
            variant="link"
            @click="handleDeleteCollection(dismiss)"
          >
            {{ t('deleteList') }}
          </TButton>
          <TButton
            v-else
            variant="link"
            @click="handleCancel(dismiss)"
          >
            {{ t('actions.cancel') }}
          </TButton>
          <TButton
            type="submit"
            :disabled="isLoading"
          >
            {{ collection ? t('actions.saveChanges') : t('favourites.createNewList') }}
          </TButton>
        </div>
      </TForm>
    </template>
  </TModalCritical>
</template>

<script setup lang="ts">
import { required } from '@regle/rules'
import { TInput } from '#components'

import type { ComponentPublicInstance } from 'vue'
import type { Nullable } from '~/types'
import type { UserFavouritesCollection } from '~/types/rental-api-aliases'

const props = withDefaults(defineProps<{
  rvId?: string
  collection?: Nullable<UserFavouritesCollection>
}>(), {
  collection: null,
})

const emit = defineEmits<{
  'close': []
  'collection-reset': []
}>()

const visible = defineModel<boolean>('visible', { required: true })

const { t } = useI18n()
const {
  favourites,
  isLoading,
  addRvToCollection,
  removeCollectionFromFavourites,
  updateCollectionInFavourites,
  addCollectionToFavourites,
} = useFavouritesData()

const collectionNameEl = ref<ComponentPublicInstance<typeof TInput>>()

const name = ref('')

const formIsLoading = ref(false)

const { r$ } = useRegle(name, {
  required,
  collectionNameIsUnique: withMessage(
    collectionNameIsUnique(
      computed(() => favourites.value.flatMap((collection) => collection.CollectionName ? [collection.CollectionName] : [])),
      () => props.collection?.CollectionName,
    ),
    () => t('errors.duplication'),
  ),
})

watch(() => props.collection, (newVal) => {
  if (newVal) {
    name.value = newVal.CollectionName || ''
  }
  else {
    reset()
  }
})

async function handleContentVisible() {
  focusElementInput(collectionNameEl.value?.$el, true)
}

async function handleSubmit(dismiss: () => void) {
  const { valid } = await r$.$validate()

  if (!valid) {
    return
  }

  if (props.collection) {
    await handleRenameCollection(dismiss)
  }
  else {
    await handleSaveNewCollection(dismiss)
  }
}

async function handleDeleteCollection(dismiss: () => void) {
  if (props.collection?.Id) {
    await removeCollectionFromFavourites(props.collection.Id)

    dismiss()
  }
}

async function handleRenameCollection(dismiss: () => void) {
  if (props.collection?.Id) {
    await updateCollectionInFavourites({
      collectionId: props.collection.Id,
      newCollectionName: name.value,
    })

    dismiss()
  }
}

const { $captureError } = useNuxtApp()
async function handleSaveNewCollection(dismiss: () => void) {
  try {
    formIsLoading.value = true

    const collection = await addCollectionToFavourites(name.value)

    // Add the RV to the collection if an RV ID was provided.
    if (props.rvId && collection?.Id) {
      await addRvToCollection(props.rvId, collection.Id)
    }

    reset()
    dismiss()
  }
  catch (e) {
    $captureError(e)
  }
  finally {
    formIsLoading.value = false
  }
}

function reset() {
  name.value = ''
  r$.$reset()
}

function handleCancel(dismiss: () => void) {
  reset()
  dismiss()
}
</script>

<i18n lang="json">
{
  "en": {
    "new": {
      "title": "New favourites list",
      "label": "Name your new list"
    },
    "edit": {
      "title": "Edit favourites list",
      "label": "Edit the name of your list"
    },
    "deleteList": "Delete list",
    "errors": {
      "duplication": "This name has already been used"
    }
  },
  "fr": {
    "new": {
      "title": "Nouvelle liste de favoris",
      "label": "Nom de votre nouvelle liste"
    },
    "edit": {
      "title": "Modifier la liste de favoris",
      "label": "Modifier le nom de votre liste"
    },
    "deleteList": "Supprimer",
    "errors": {
      "duplication": "Ce nom a déjà été utilisé"
    }
  }
}
</i18n>
